<template>
  <div
    v-if="rightBarContent"
    :class="
      rightBarVisibility
        ? 'col-span-3 xl:pt-6 xl:w-full md:w-[18.2rem] w-[260px]'
        : 'col-span-1 bg-transparent hidden'
    "
    class="xl:static absolute top-[52px] right-0 z-[9] rightbar-carot"
  >
    <div v-if="rightBarVisibility">
      <div
        v-if="rightBarContent.header"
        ref="rightBarButton"
        class="text-center px-2.5 xl:block hidden"
      >
        <SplitButton
          v-for="(button, index) in rightBarContent.header.buttons"
          :key="index + button.title"
          v-permission="button.permissions"
          :buttons="button"
        />
      </div>
      <!-- User Profile -->
      <div
        v-if="isStatsAvailable"
        ref="rightBarContainer"
        class="bg-white p-5 border border-transparent overflow-y-auto rounded-lg relative"
        :style="`height: ${Math.max(rightBarHeight, 0)}px`"
      >
        <ProfileInfo
          v-for="(info, index) in rightBarContent.profileInfo"
          :key="index"
          :profile-info="info"
        />
        <!-- Stats -->
        <template v-if="rightBarContent.userInfo">
          <RightBarStats
            v-for="(stat, idx) in rightBarContent.userInfo"
            :key="stat.title + '-' + idx"
            :stats="stat"
          />
        </template>
        <template v-if="rightBarContent.stats">
          <RightBarStats
            v-for="(stat, idx) in rightBarContent.stats"
            :key="stat.title + '-' + idx"
            :stats="stat"
          />
        </template>

        <!-- Graphs -->
        <div v-permission="graphsPermissions">
          <template v-if="isGraphDataEmpty(rightBarContent?.chartData)">
            <NoRecordFound custom-class="text-xl" text="NO_STATS_FOUND" />
          </template>
          <template v-else>
            <div v-for="(graph, index) in rightBarContent.chartData" :key="index">
              <div v-if="!isEmpty(graph)">
                <h3
                  v-if="graph.GRAPH_TITLE && !isEmpty(graph.GRAPH_TITLE)"
                  class="font-rubik font-medium text-text-color text-base md:text-xl text-center"
                >
                  {{ graph.GRAPH_TITLE }}
                </h3>
                <GraphView :chart-data="graph.chart" />
                <template v-if="graph.LEGEND_DATA && graph.LEGEND_DATA.length">
                  <Status
                    v-for="status in graph.LEGEND_DATA"
                    :key="status.title"
                    :status="status"
                    :graph-name="graph.chart.type"
                  />
                </template>
                <Divider :class="lastDivider(rightBarContent.chartData, index)" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RightBarStats from '@components/RightBarStats.vue'
import SplitButton from '@components/SplitButton.vue'
import ProfileInfo from '@components/ProfileInfo.vue'
import { mapState } from 'vuex'
import GraphView from '@src/router/views/graph/graphView.vue'
import isEmpty from 'lodash/isEmpty'
import { ENV } from '@src/constants/general-constants.js'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import Divider from '@/src/components/Divider.vue'
import Status from '@/src/components/status.vue'
import GENERAL_CONSTANT from '@src/constants/general-constants.js'

export default {
  components: {
    RightBarStats,
    ProfileInfo,
    SplitButton,
    GraphView,
    NoRecordFound,
    Divider,
    Status,
  },
  props: {
    rightBarVisibility: { type: Boolean },
    mobileRightBarHeight: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      rightBar: 'rightBar',
      stats: null,
      graphsPermissions: {
        env: [ENV.DEV],
      },
      rightBarButtonHeight: 0,
      headerHeight: 150,
      windowInnerHeight: 0,
      rightBarHeight: 0,
    }
  },
  computed: {
    ...mapState({
      rightBarContent: (state) => state.layout.rightBarContent,
      activeEvent: (state) => state.layout.activeEvent,
      tabRightNav: (state) => state.layout.tabRightNav,
    }),
    isStatsAvailable() {
      return this.rightBarContent?.stats?.length
    },
    rightBarHight() {
      return this.$refs.rightBarButton
    },
  },
  watch: {
    mobileRightBarHeight: {
      handler(val) {
        if (val) {
          this.setRightBarHeight()
        }
      },
    },
    'rightBarContent.header': {
      handler() {
        this.$nextTick(() => {
          this.setRightBarButtonHeight()
        })
      },
      deep: true,
      immediate: true,
    },
  },

  mounted() {
    this.setRightBarHeight()
    this.$nextTick(() => {
      this.setRightBarButtonHeight()
    })

    this.resizeObserver = new ResizeObserver(() => {
      this.setRightBarButtonHeight()
    })

    if (this.$refs.rightBarButton) {
      this.resizeObserver.observe(this.$refs.rightBarButton)
    }

    // Initialize and listen for window/viewport resize to update available height
    this.updateWindowInnerHeight()
    window.addEventListener('resize', this.updateWindowInnerHeight)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateWindowInnerHeight)
    }

    // Also recalc container height after initial render
    this.$nextTick(() => {
      this.updateContainerHeight()
    })
  },
  beforeUnmount() {
    if (this.resizeObserver && this.$refs.rightBarButton) {
      this.resizeObserver.unobserve(this.$refs.rightBarButton)
    }
    window.removeEventListener('resize', this.updateWindowInnerHeight)
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateWindowInnerHeight)
    }
  },

  methods: {
    isEmpty,
    setRightBarHeight() {
      const rightBarContainer = this.$refs['rightBarContainer']
      if (rightBarContainer && this.mobileRightBarHeight) {
        rightBarContainer.style.height = `${this.mobileRightBarHeight - 40}px`
      }
    },
    lastDivider(chartData, index) {
      return chartData.length === index + 1 ? 'hidden' : ''
    },
    isGraphDataEmpty(graphs) {
      return graphs?.every((graph) => {
        const datasets = graph?.chart?.data?.datasets || []
        return datasets?.every((dataset) => {
          const data = dataset?.data || []
          return data?.every((value) => value === 0)
        })
      })
    },
    setRightBarButtonHeight() {
      const el = this.$refs.rightBarButton
      if (el) {
        this.rightBarButtonHeight =
          el.offsetHeight ||
          el.scrollHeight ||
          el.getBoundingClientRect().height ||
          GENERAL_CONSTANT.EMPTY_RECORD
      } else {
        this.rightBarButtonHeight = 0
      }
      // Any change in button height can affect container's top; recalc height
      this.updateContainerHeight()
    },
    updateWindowInnerHeight() {
      if (typeof window !== 'undefined') {
        const viewportHeight = window.visualViewport?.height
        this.windowInnerHeight = Math.round(viewportHeight || window.innerHeight)
      }
      this.updateContainerHeight()
    },
    updateContainerHeight() {
      const container = this.$refs['rightBarContainer']
      if (!container) return
      const viewport =
        this.windowInnerHeight ||
        (typeof window !== 'undefined' ? window.innerHeight : GENERAL_CONSTANT.EMPTY_RECORD)
      const topOffset = container?.getBoundingClientRect()?.top || GENERAL_CONSTANT.EMPTY_RECORD
      const bottomSpacing = 10
      this.rightBarHeight = Math.floor(viewport - topOffset - bottomSpacing)
    },
  },
}
</script>

<style lang="scss" scoped>
::-webkit-scrollbar {
  display: none;
}
@media (max-width: 1279px) {
  .rightbar-carot {
    border-radius: 8px;
    &::before {
      position: absolute;
      top: -5px;
      right: 11px;
      width: 14px;
      height: 15px;
      content: '';
      background-color: var(--bg-white);
      transform: rotate(314deg);
      clip-path: polygon(0% -10%, 100% 0%, 115% 100%);
    }
  }
}
</style>
